import * as z from 'zod';

import { isoDateTimeToMoment } from './codecs/moment';
import { AddressSchema } from './address';

export const AuditTypeSchema = z.enum(['ea', 'bca']);
export type AuditType = z.infer<typeof AuditTypeSchema>;

export const AuditSchema = z.object({
  /**
   * The unique identifier of the audit.
   */
  id: z.string().nonempty(),

  /**
   * The title of the audit.
   */
  title: z.string().nonempty(),

  /**
   * The ID of the project this audit belongs to.
   */
  project: z.string().nonempty().optional(),

  /**
   * The audit group this audit belongs to.
   * If not provided, the audit will be considered as a standalone audit. Otherwise, it will be grouped with other audits that have the same audit group ID.
   */
  group: z.string().nonempty().optional(),

  /**
   * The address of the audit.
   */
  address: AddressSchema.optional(),

  /**
   * The types of the audit.
   */
  types: z.array(AuditTypeSchema).nonempty(),

  /**
   * The local date and time when the audit is scheduled to be conducted.
   */
  auditDate: isoDateTimeToMoment.nonoptional(),

  /**
   * The local date and time when the audit was created.
   */
  createdAt: isoDateTimeToMoment.nonoptional(),

  /**
   * The local date and time when the audit was last updated.
   */
  updatedAt: isoDateTimeToMoment.nonoptional(),
});

export type Audit = z.infer<typeof AuditSchema>;
export type SerializedAudit = ReturnType<typeof AuditSchema.encode>;
export type AuditId = Audit['id'];

export type NewAudit = Pick<Audit, 'title' | 'types'>;

export class AuditNotFoundError extends Error {
  constructor(auditId: AuditId) {
    super(`Audit with ID ${auditId} not found`);
    this.name = 'AuditNotFoundError';
  }
}
